import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users, Calendar, MessageSquare, UserCog, Trophy, ScrollText, Activity, Images } from "lucide-react";
import { sportsApi, eventsApi, socialApi, adminApi, trophiesApi, disciplineApi, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageContainer, Loading, EmptyState, UserAvatar, SportChip, SectionTitle } from "@/components/shared";
import { SportCover, StaggerGroup, StaggerItem } from "@/components/motion";
import { SportGallery } from "@/components/gallery/SportGallery";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-white backdrop-blur">
    <Icon className="h-4 w-4 text-secondary" /><div><div className="text-lg font-heading font-bold leading-none">{value}</div><div className="text-[10px] text-white/70">{label}</div></div>
  </div>
);

export default function SportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sport, setSport] = useState(null);
  const [events, setEvents] = useState([]);
  const [posts, setPosts] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coaches, setCoaches] = useState([]);
  const [coachId, setCoachId] = useState("");
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, e, p, lb, rl] = await Promise.all([
        sportsApi.detail(id), eventsApi.list({ sport_id: id }), socialApi.feed(id),
        trophiesApi.leaderboard({ sport_id: id }), disciplineApi.rules(id),
      ]);
      setSport(s.data); setEvents(e.data); setPosts(p.data.slice(0, 6)); setLeaders(lb.data.slice(0, 8)); setRules(rl.data); setCoachId(s.data.coach_id || "");
    } catch (err) { toast.error(errMsg(err)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const openManage = async () => { try { const r = await adminApi.coaches(); setCoaches(r.data); } catch {} setOpen(true); };
  const assignCoach = async () => { try { await sportsApi.update(id, { name: sport.name, coach_id: coachId }); toast.success("Entraîneur assigné"); setOpen(false); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (loading) return <PageContainer><Loading /></PageContainer>;
  if (!sport) return <PageContainer><EmptyState title="Sport introuvable" /></PageContainer>;

  return (
    <div>
      <SportCover sport={sport} className="min-h-[220px]">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-7 text-white">
          <button onClick={() => navigate("/sports")} className="mb-3 inline-flex items-center gap-1 text-sm text-white/80 hover:text-white" data-testid="sport-retour"><ArrowLeft className="h-4 w-4" /> Sports</button>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <SportChip name={sport.name} color={sport.color} className="bg-white/90" />
              <h1 className="mt-2 font-heading text-3xl font-bold drop-shadow">{sport.name}</h1>
              <p className="mt-1 max-w-xl text-sm text-white/80">{sport.description}</p>
            </div>
            {user?.role === "admin" && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild><Button variant="outline" className="border-white/30 bg-white/10 text-white hover:bg-white/20" onClick={openManage} data-testid="sport-gerer-bouton"><UserCog className="mr-1.5 h-4 w-4" /> Assigner entraîneur</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Assigner un entraîneur à {sport.name}</DialogTitle></DialogHeader>
                  <Select value={coachId} onValueChange={setCoachId}><SelectTrigger data-testid="sport-coach-select"><SelectValue placeholder="Choisir un membre" /></SelectTrigger><SelectContent>{coaches.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name}</SelectItem>)}</SelectContent></Select>
                  <DialogFooter><Button onClick={assignCoach} data-testid="sport-coach-enregistrer">Enregistrer</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <Stat icon={Users} label="Membres actifs" value={sport.active_members_count} />
            <Stat icon={Activity} label="Effectif total" value={sport.member_count} />
            <Stat icon={Trophy} label="Trophées" value={sport.trophies_count} />
            <Stat icon={Calendar} label="Évènements" value={events.length} />
          </div>
        </div>
      </SportCover>

      <PageContainer>
        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-1"><CardContent className="p-5">
            <h3 className="mb-3 font-heading font-semibold">Entraîneur</h3>
            {sport.coach ? (
              <Link to={`/profil/${sport.coach.id}`} className="flex items-center gap-3"><UserAvatar user={sport.coach} /><div><div className="font-medium">{sport.coach.first_name} {sport.coach.last_name}</div><div className="text-xs text-muted-foreground">Entraîneur attitré</div></div></Link>
            ) : <p className="text-sm text-muted-foreground">Aucun entraîneur assigné.</p>}
          </CardContent></Card>

          <div className="lg:col-span-2">
            <Tabs defaultValue="roster">
              <TabsList className="flex-wrap">
                <TabsTrigger value="roster" data-testid="tab-effectif"><Users className="mr-1.5 h-4 w-4" />Effectif</TabsTrigger>
                <TabsTrigger value="events" data-testid="tab-events"><Calendar className="mr-1.5 h-4 w-4" />Évènements</TabsTrigger>
                <TabsTrigger value="trophies" data-testid="tab-trophies"><Trophy className="mr-1.5 h-4 w-4" />Trophées</TabsTrigger>
                <TabsTrigger value="gallery" data-testid="tab-gallery"><Images className="mr-1.5 h-4 w-4" />Galerie</TabsTrigger>
                <TabsTrigger value="feed" data-testid="tab-feed"><MessageSquare className="mr-1.5 h-4 w-4" />Fil</TabsTrigger>
                <TabsTrigger value="rules" data-testid="tab-rules"><ScrollText className="mr-1.5 h-4 w-4" />Règles</TabsTrigger>
              </TabsList>
              <TabsContent value="roster" className="mt-4">
                {sport.roster?.length ? (
                  <StaggerGroup className="grid gap-3 sm:grid-cols-2">{sport.roster.map((m) => (
                    <StaggerItem key={m.id}><Link to={`/profil/${m.id}`}><Card className="card-hover"><CardContent className="flex items-center gap-3 p-3"><UserAvatar user={m} size="h-9 w-9" /><span className="text-sm font-medium">{m.first_name} {m.last_name}</span></CardContent></Card></Link></StaggerItem>
                  ))}</StaggerGroup>
                ) : <EmptyState title="Aucun membre actif" icon={Users} />}
              </TabsContent>
              <TabsContent value="events" className="mt-4 space-y-3">
                {events.length ? events.map((e) => (
                  <Card key={e.id}><CardContent className="flex items-center gap-4 p-4">
                    <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl bg-primary text-primary-foreground"><span className="text-[10px] uppercase">{new Date(e.start).toLocaleDateString("fr-CA", { month: "short" })}</span><span className="text-lg font-bold leading-none">{new Date(e.start).getDate()}</span></div>
                    <div><div className="font-semibold">{e.title}</div><div className="text-sm text-muted-foreground">{new Date(e.start).toLocaleString("fr-CA", { hour: "2-digit", minute: "2-digit" })} · {e.location || "—"}</div></div>
                  </CardContent></Card>
                )) : <EmptyState title="Aucun évènement" icon={Calendar} />}
              </TabsContent>
              <TabsContent value="trophies" className="mt-4">
                {leaders.length ? (
                  <Card><CardContent className="divide-y p-0">{leaders.map((row, i) => (
                    <Link to={`/profil/${row.recipient.id}`} key={row.recipient.id} className="flex items-center gap-3 p-3 hover:bg-muted">
                      <span className="w-6 text-center font-heading font-bold text-muted-foreground">{i + 1}</span>
                      <UserAvatar user={row.recipient} size="h-8 w-8" />
                      <span className="flex-1 text-sm font-medium">{row.recipient.first_name} {row.recipient.last_name}</span>
                      <span className="inline-flex items-center gap-1 text-sm font-semibold"><Trophy className="h-3.5 w-3.5 text-trophy-gold" />{row.count}</span>
                    </Link>
                  ))}</CardContent></Card>
                ) : <EmptyState title="Aucun trophée attribué" icon={Trophy} />}
              </TabsContent>
              <TabsContent value="gallery" className="mt-4">
                <SportGallery sportId={id} isAdmin={user?.role === "admin"} />
              </TabsContent>
              <TabsContent value="feed" className="mt-4 space-y-3">
                {posts.length ? posts.map((p) => (
                  <Card key={p.id}><CardContent className="p-4"><div className="flex items-center gap-2"><UserAvatar user={p.author || { first_name: "A", last_name: "S" }} size="h-8 w-8" /><span className="text-sm font-medium">{p.author ? `${p.author.first_name} ${p.author.last_name}` : "ASL (automatique)"}</span></div><p className="mt-2 text-sm">{p.content}</p></CardContent></Card>
                )) : <EmptyState title="Aucune publication" icon={MessageSquare} />}
                <Link to="/social" className="block text-center text-sm text-secondary hover:underline">Voir le fil complet</Link>
              </TabsContent>
              <TabsContent value="rules" className="mt-4 space-y-3">
                {rules.length ? rules.map((r) => (
                  <Card key={r.id}><CardContent className="p-4"><div className="font-heading font-semibold">{r.title}</div><p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{r.content}</p></CardContent></Card>
                )) : <EmptyState title="Aucun règlement spécifique" description="Consultez les règlements généraux." icon={ScrollText} action={<Button variant="outline" size="sm" onClick={() => navigate("/reglements")}>Règlements</Button>} />}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </PageContainer>
    </div>
  );
}
